import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { PageWithTitle } from 'src/Common/components';
import {
  useCosts,
  useCostsMonthly,
  useCreateCost,
  useDeleteCost,
  useRealPnl,
  usePartnerPayments,
  useCreatePartnerPayment,
  useDeletePartnerPayment,
  PredictionCost,
  PartnerPayment,
} from '../api/useCosts';
import { useCashflows } from '../api/usePositions';
import { formatUsd, pnlColor } from '../utils/format';
import { DailyPnlTab } from '../components/DailyPnlTab';

const fmtDate = (value: string | undefined | null) => {
  if (!value) return '-';
  return new Date(value).toISOString().split('T')[0];
};

const SummaryCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Paper
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 180,
      backgroundColor: 'background.paper',
    }}
  >
    <Typography
      sx={{
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        color: 'grey.600',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color }}>
      {value}
    </Typography>
  </Paper>
);

// Record a monthly payment and close the month. The current month's P&L is
// shown so the amount can be sized against it; submitting freezes the Real
// P&L snapshot that becomes next month's baseline.
const RecordPaymentDialog = ({
  open,
  onClose,
  currentMonthPnl,
}: {
  open: boolean;
  onClose: () => void;
  currentMonthPnl: number | undefined;
}) => {
  const { mutate: create, isLoading } = useCreatePartnerPayment();
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('external');
  const [platform, setPlatform] = useState('kalshi');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [manualOverride, setManualOverride] = useState(false);
  const [manualPnl, setManualPnl] = useState('');

  const canSubmit = useMemo(() => {
    const num = parseFloat(amount);
    if (isNaN(num) || num < 0) return false;
    if (manualOverride && isNaN(parseFloat(manualPnl))) return false;
    return true;
  }, [amount, manualOverride, manualPnl]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    create(
      {
        paymentUsd: parseFloat(amount),
        paymentSource: source,
        paymentPlatform: source === 'platform' ? platform : undefined,
        settledAt: new Date(date).toISOString(),
        note: note.trim() || undefined,
        monthPnlUsd: manualOverride ? parseFloat(manualPnl) : undefined,
      },
      {
        onSuccess: () => {
          setAmount('');
          setNote('');
          setManualOverride(false);
          setManualPnl('');
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem' }}>
        Record Payment / Close Month
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.8rem', color: 'grey.500', mb: 1 }}>
          Current month P&amp;L so far:{' '}
          <Box
            component="span"
            sx={{ color: pnlColor(currentMonthPnl), fontWeight: 600 }}
          >
            {formatUsd(currentMonthPnl)}
          </Box>
        </Typography>
        <FormControlLabel
          sx={{ mb: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={manualOverride}
              onChange={e => {
                const on = e.target.checked;
                setManualOverride(on);
                // Prefill with the live figure so the operator only tweaks it.
                if (on && manualPnl === '' && currentMonthPnl != null) {
                  setManualPnl(String(currentMonthPnl));
                }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.8rem' }}>
              Enter month P&amp;L manually
            </Typography>
          }
        />
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {manualOverride && (
            <TextField
              size="small"
              label="Month P&L (USD)"
              type="number"
              value={manualPnl}
              onChange={e => setManualPnl(e.target.value)}
              fullWidth
              helperText="Overrides the live computed P&L for this month's close. May be negative."
            />
          )}
          <TextField
            size="small"
            label="Payment Amount (USD)"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            size="small"
            select
            label="Source"
            value={source}
            onChange={e => setSource(e.target.value)}
            fullWidth
          >
            <MenuItem value="external">
              External (paid outside the platform)
            </MenuItem>
            <MenuItem value="platform">
              Platform (withdrawn from a balance)
            </MenuItem>
          </TextField>
          {source === 'platform' && (
            <TextField
              size="small"
              select
              label="Platform"
              value={platform}
              onChange={e => setPlatform(e.target.value)}
              fullWidth
              helperText="A matching withdrawal is logged automatically."
            >
              <MenuItem value="kalshi">Kalshi</MenuItem>
              <MenuItem value="polymarket">Polymarket</MenuItem>
            </TextField>
          )}
          <TextField
            size="small"
            label="Date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            label="Note"
            value={note}
            onChange={e => setNote(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSubmit || isLoading}
          onClick={handleSubmit}
        >
          Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type EconomicsTab = 'daily' | 'costs';

export const EconomicsPage = () => {
  const [tab, setTab] = useState<EconomicsTab>('daily');
  const { data: realPnl } = useRealPnl();
  const { data: monthly } = useCostsMonthly();
  const { data: costs } = useCosts();
  const { data: cashflows } = useCashflows();
  const { data: payments } = usePartnerPayments();
  const { mutate: createCost, isLoading: creating } = useCreateCost();
  const { mutate: deleteCost } = useDeleteCost();
  const { mutate: deletePayment } = useDeletePartnerPayment();

  const cashflowSummary = useMemo(() => {
    let deposits = 0;
    let withdrawals = 0;
    for (const cf of cashflows ?? []) {
      if (cf.type === 'deposit') deposits += cf.amountUsd;
      else withdrawals += cf.amountUsd;
    }
    return { deposits, withdrawals, net: deposits - withdrawals };
  }, [cashflows]);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [confirmDelete, setConfirmDelete] = useState<PredictionCost | null>(
    null
  );
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [confirmDeletePayment, setConfirmDeletePayment] =
    useState<PartnerPayment | null>(null);

  const canSubmit = useMemo(() => {
    const num = parseFloat(amount);
    return description.trim().length > 0 && !isNaN(num) && num > 0;
  }, [description, amount]);

  const handleAdd = () => {
    if (!canSubmit) return;
    createCost(
      {
        description: description.trim(),
        amountUsd: parseFloat(amount),
        date: new Date(date).toISOString(),
      },
      {
        onSuccess: () => {
          setDescription('');
          setAmount('');
        },
      }
    );
  };

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        <Stack direction="row" alignItems="center" mb={1.5}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Economics
          </Typography>
        </Stack>

        {/* Tabs — mirrors the compact 32px pattern from PositionsPage. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0,
                textTransform: 'none',
                fontSize: '0.82rem',
              },
              '& .MuiTabs-indicator': { height: 2 },
            }}
          >
            <Tab value="daily" label="Daily P&L" />
            <Tab value="costs" label="Costs & Payments" />
          </Tabs>
        </Stack>

        {tab === 'daily' && <DailyPnlTab />}

        {tab === 'costs' && (
          <>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2}>
          <SummaryCard
            label="P&L"
            value={formatUsd(realPnl?.pnlUsd)}
            color={pnlColor(realPnl?.pnlUsd)}
          />
          <SummaryCard
            label="Total Costs"
            value={formatUsd(realPnl?.totalCostsUsd)}
            color="#f44336"
          />
          <SummaryCard
            label="Real P&L"
            value={formatUsd(realPnl?.realPnlUsd)}
            color={pnlColor(realPnl?.realPnlUsd)}
          />
          <SummaryCard
            label="This Month P&L"
            value={formatUsd(payments?.currentPeriod.periodRealPnlUsd)}
            color={pnlColor(payments?.currentPeriod.periodRealPnlUsd)}
          />
          <SummaryCard
            label="Paid Out"
            value={formatUsd(payments?.totalPaidUsd)}
            color="#ff9800"
          />
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              color: 'grey.600',
              mb: 1,
            }}
          >
            Add Cost
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              sx={{ flex: 2, minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Amount (USD)"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!canSubmit || creating}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 320 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              Month-by-Month
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Costs</TableCell>
                  <TableCell align="right"># Entries</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthly?.monthly.map(row => (
                  <TableRow key={row.month}>
                    <TableCell>{row.month}</TableCell>
                    <TableCell align="right">
                      {formatUsd(row.totalUsd)}
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
                {monthly && monthly.monthly.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 1.5, flex: 2, minWidth: 420 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              All Costs
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {costs?.map(cost => (
                  <TableRow key={cost._id}>
                    <TableCell>
                      {new Date(cost.date).toISOString().split('T')[0]}
                    </TableCell>
                    <TableCell>{cost.description}</TableCell>
                    <TableCell align="right">
                      {formatUsd(cost.amountUsd)}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete(cost)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {costs && costs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Stack>

        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mb={1}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
              }}
            >
              Monthly P&amp;L &amp; Payments
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setPaymentOpen(true)}
            >
              Record Payment
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="right">Period Real P&amp;L</TableCell>
                <TableCell align="right">Payment</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: 'rgba(33,150,243,0.08)' }}>
                <TableCell>
                  {payments
                    ? `${fmtDate(payments.currentPeriod.startDate)} → now`
                    : '-'}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: '#2196f3',
                    }}
                  >
                    IN PROGRESS
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: pnlColor(payments?.currentPeriod.periodRealPnlUsd),
                  }}
                >
                  {formatUsd(payments?.currentPeriod.periodRealPnlUsd)}
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell>—</TableCell>
                <TableCell>—</TableCell>
                <TableCell />
              </TableRow>
              {[...(payments?.payments ?? [])].reverse().map(p => (
                <TableRow key={p._id}>
                  <TableCell>
                    {fmtDate(p.periodStart)} → {fmtDate(p.periodEnd)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: pnlColor(p.periodRealPnlUsd) }}
                  >
                    {formatUsd(p.periodRealPnlUsd)}
                    {p.snapshotIsManual && (
                      <Box
                        component="span"
                        title="P&L entered manually"
                        sx={{ ml: 0.5, fontSize: '0.6rem', color: 'grey.500' }}
                      >
                        ✎
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatUsd(p.paymentUsd)}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {p.paymentSource}
                    {p.paymentPlatform ? ` (${p.paymentPlatform})` : ''}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                    {p.note || '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 40 }}>
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDeletePayment(p)}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {payments && payments.payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                      No monthly closes yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mb={1}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
              }}
            >
              Deposits &amp; Withdrawals
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Deposits:{' '}
                <Box component="span" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  {formatUsd(cashflowSummary.deposits)}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Withdrawals:{' '}
                <Box component="span" sx={{ color: '#f44336', fontWeight: 600 }}>
                  {formatUsd(cashflowSummary.withdrawals)}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Net Flow:{' '}
                <Box
                  component="span"
                  sx={{ color: pnlColor(cashflowSummary.net), fontWeight: 600 }}
                >
                  {formatUsd(cashflowSummary.net)}
                </Box>
              </Typography>
            </Stack>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cashflows?.map(cf => (
                <TableRow key={cf._id}>
                  <TableCell>
                    {new Date(cf.date).toISOString().split('T')[0]}
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {cf.platform}
                  </TableCell>
                  <TableCell
                    sx={{
                      textTransform: 'capitalize',
                      color: cf.type === 'deposit' ? '#4caf50' : '#f44336',
                    }}
                  >
                    {cf.type}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: cf.type === 'deposit' ? '#4caf50' : '#f44336',
                    }}
                  >
                    {cf.type === 'withdrawal' ? '-' : '+'}
                    {formatUsd(cf.amountUsd)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                    {cf.note || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {cashflows && cashflows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      sx={{ fontSize: '0.75rem', color: 'grey.600' }}
                    >
                      No deposits or withdrawals yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Dialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete cost?</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.82rem' }}>
              {confirmDelete?.description} — {formatUsd(confirmDelete?.amountUsd)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDelete) {
                  deleteCost(confirmDelete._id);
                  setConfirmDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <RecordPaymentDialog
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          currentMonthPnl={payments?.currentPeriod.periodRealPnlUsd}
        />

        <Dialog
          open={!!confirmDeletePayment}
          onClose={() => setConfirmDeletePayment(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>
            Delete payment?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.82rem' }}>
              Payment of {formatUsd(confirmDeletePayment?.paymentUsd)} on{' '}
              {fmtDate(confirmDeletePayment?.settledAt)}.
              {confirmDeletePayment?.paymentSource === 'platform' &&
                ' The linked withdrawal will also be removed.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDeletePayment(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDeletePayment) {
                  deletePayment(confirmDeletePayment._id);
                  setConfirmDeletePayment(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
          </>
        )}
      </Box>
    </PageWithTitle>
  );
};
